import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, Sparkles, Flame } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import GoogleLoginButton from '../components/GoogleLoginButton';

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';
  
  // Show error message from Google OAuth if present
  useEffect(() => {
    if (location.state?.error) {
      toast.error(location.state.error);
    }
  }, [location.state?.error]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await login(email, password);
      toast.success('Welcome back!');
      
      if (result.needsDonationSetup) {
        navigate('/donations');
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center gap-2 mb-12">
            <Flame size={32} className="text-[#E63946]" />
            <span className="text-4xl font-teko font-semibold text-gradient">Supremics</span>
          </Link>
          
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-teko font-semibold mb-2">Welcome back</h1>
            <p className="text-[var(--text-secondary)]">
              Sign in to continue reading your favorite comics
            </p>
          </div>
          
          {/* Google Sign In */}
          <div className="mb-6">
            <GoogleLoginButton mode="login" />
          </div>
          
          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-sm text-[var(--text-secondary)]">or continue with email</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="h-12 bg-[var(--surface)] border-[var(--border)] focus:border-[var(--primary)]"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 bg-[var(--surface)] border-[var(--border)] focus:border-[var(--primary)] pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-[var(--border)]" />
                <span className="text-sm text-[var(--text-secondary)]">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-[var(--primary)] hover:underline">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className="text-center text-[var(--text-secondary)] mt-6">
            Do not have an account?{' '}
            <Link to="/signup" className="text-[var(--primary)] hover:underline font-medium" data-testid="signup-link">
              Create one
            </Link>
          </p>

          {/* Demo Credentials */}
          <div className="mt-8 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl">
            <p className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider mb-2">Demo Credentials</p>
            <div className="space-y-1 text-sm">
              <p className="text-[var(--text-secondary)]">
                <span className="text-[var(--foreground)]">Admin:</span> admin@supremics.com / admin123
              </p>
              <p className="text-[var(--text-secondary)]">
                <span className="text-[var(--foreground)]">Creator:</span> mangamaster@supremics.com / creator123
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[var(--primary)] to-[var(--secondary)] items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 border border-white rounded-full" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 border border-white rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white rounded-full" />
        </div>
        
        <div className="relative z-10 text-center text-white p-12">
          <Sparkles size={64} className="mx-auto mb-6 opacity-80" />
          <h2 className="text-5xl font-teko font-semibold mb-4">Read. Create. Earn.</h2>
          <p className="text-white/80 text-lg max-w-md mx-auto">
            Join thousands of creators and readers in the ultimate comic platform.
          </p>
          
          <div className="flex justify-center gap-8 mt-12">
            <div className="text-center">
              <p className="text-4xl font-teko">10K+</p>
              <p className="text-sm text-white/70">Comics</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-teko">50K+</p>
              <p className="text-sm text-white/70">Creators</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-teko">1M+</p>
              <p className="text-sm text-white/70">Readers</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
